/**
 * Process scanner data into grouped agencies and formatted scanner entries.
 *
 * Scanner ID format: "AgencyID-ScannerID_Ext"
 * Also injects the current login account if not already present.
 *
 * @param {Array}  scannerData  - Raw scanner array from API [{ID, Value}, ...]
 * @param {Array}  arrAgencies  - Agency lookup array [{ID, Value}, ...]
 * @returns {{ agencies: Array, formattedScanners: Array }}
 */
export function processScannersByAgency(scannerData, arrAgencies) {
  if (!scannerData || !Array.isArray(scannerData)) {
    return { agencies: [], formattedScanners: [] };
  }

  const agencyMap = new Map();
  const formattedScanners = [];

  // Process each scanner to extract agency ID and format for MultiSelection
  scannerData.forEach(scanner => {
    const parts = scanner.ID.split('-');
    if (parts.length < 2) return;

    const agencyId = parts[0];
    const scannerLabel = scanner.Value || scanner.ID;

    formattedScanners.push({
      ID: scanner.ID,
      Value: scannerLabel,
      GroupID: agencyId,
    });

    // Track unique agencies
    if (!agencyMap.has(agencyId)) {
      const agencyInfo = arrAgencies
        ? arrAgencies.find(a => a.ID === agencyId)
        : null;

      agencyMap.set(agencyId, {
        ID: agencyId,
        Name: agencyInfo ? agencyInfo.Value : agencyId,
      });
    }
  });

  // Inject login account's scanner if not already present
  try {
    const raw = localStorage.getItem("FWUser");
    if (raw) {
      const loginAccount = JSON.parse(raw);
      const info = loginAccount?.Information;
      if (info) {
        const loginAgencyId = info.User_ID;

        if (!agencyMap.has(loginAgencyId)) {
          agencyMap.set(loginAgencyId, {
            ID: loginAgencyId,
            Name: info.AgencyName || info.Agency || loginAgencyId,
          });
        }

        const loginScannerId = `${info.User_ID}-${info.User_ID_Ext}`;
        if (!formattedScanners.find(s => s.ID === loginScannerId)) {
          formattedScanners.push({
            ID: loginScannerId,
            Value: info.Name,
            GroupID: loginAgencyId,
          });
        }
      }
    }
  } catch {
    // Ignore localStorage parse errors
  }

  return {
    agencies: Array.from(agencyMap.values()),
    formattedScanners,
  };
}
